import React, { memo, useCallback, useEffect, useRef } from 'react';

import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TriggerDirection } from '@pulse-web-ui/tooltip';

import type { PersonalDocumentsFormProps } from '@entities/agent-contract';

import {
  DaDataFmsUnitController,
  DatepickerController,
  FormControlContainer,
  InputController,
  PersonalDocumentMaxLength,
  TwoColumnContainer,
  allowOnlyNumbers,
  getDateForAge,
} from '@shared/index';

import { InnTooltipText } from '../inn-tooltip-text';
import { SnilsTooltipText } from '../snils-tooltip-text/snils-tooltip-text';
import {
  DEPARTMENT_CODE,
  PASSPORT_SERIES_AND_NUMBER_MASK,
  SNILS_MASK,
} from './constants';

const DEPARTMENT_CODE_PLACEHOLDER = '000-000';

export const PersonalDocuments = memo(() => {
  const innRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
  } = useFormContext<PersonalDocumentsFormProps>();

  useEffect(() => {
    const onlyNumValue = allowOnlyNumbers(innRef.current?.value || '');
    setValue('inn', onlyNumValue);
  }, [watch('inn')]);

  const codeDepartment = watch('codeDepartment');

  const maxIssueDate = new Date();
  const minIssueDate = getDateForAge(100);

  const clearServerErrors = useCallback(() => {
    if (errors.root?.serverError) {
      clearErrors('root.serverError');
    }
  }, [errors.root]);

  return (
    <>
      <TwoColumnContainer $marginBottom={8}>
        <InputController
          control={control}
          name='passportSeriesAndNumber'
          label={t('PERSONAL_DOCUMENTS:labels.passportSeriesAndNumber')}
          id='passportSeriesAndNumber'
          onChange={clearServerErrors}
          placeholder={'0000-000000'}
          mask={PASSPORT_SERIES_AND_NUMBER_MASK}
          testId='personalDocuments.seriesAndNumber'
        />
        <DatepickerController
          control={control}
          name='dateIssue'
          id='dateIssue'
          label={t('PERSONAL_DOCUMENTS:labels.issueDate')}
          onChange={clearServerErrors}
          minDate={minIssueDate}
          maxDate={maxIssueDate}
          testId='personalDocuments.issueDate'
          showMonthDropdown
          showYearDropdown
          showInput
        />
      </TwoColumnContainer>
      <TwoColumnContainer $marginBottom={8}>
        <InputController
          control={control}
          name='codeDepartment'
          id='codeDepartment'
          label={t('PERSONAL_DOCUMENTS:labels.departmentCode')}
          onChange={clearServerErrors}
          placeholder={DEPARTMENT_CODE_PLACEHOLDER}
          mask={DEPARTMENT_CODE}
          testId='personalDocuments.departmentCode'
        />
      </TwoColumnContainer>
      <FormControlContainer $margin='0 0 16px 0'>
        <DaDataFmsUnitController
          control={control}
          name='nameDepartment'
          unitCode={codeDepartment}
          uid='nameDepartment'
          label={t('PERSONAL_DOCUMENTS:labels.departmentName')}
          onChange={clearServerErrors}
          testId='personalDocuments.departmentName'
        />
      </FormControlContainer>
      <TwoColumnContainer $marginBottom={8}>
        <InputController
          control={control}
          name='snils'
          id='snils'
          label={t('PERSONAL_DOCUMENTS:labels.snils')}
          onChange={clearServerErrors}
          placeholder={'000-000-000 00'}
          mask={SNILS_MASK}
          hintObject={{ showIcon: true }}
          trigger={TriggerDirection.CLICK}
          tooltipText={<SnilsTooltipText />}
          testId='personalDocuments.snils'
          hasTooltip
        />
        <InputController
          control={control}
          name='inn'
          id='inn'
          label={t('PERSONAL_DOCUMENTS:labels.inn')}
          ref={innRef}
          onChange={clearServerErrors}
          placeholder={'770000000000'}
          maxLength={PersonalDocumentMaxLength.INN}
          hintObject={{ showIcon: true }}
          trigger={TriggerDirection.CLICK}
          tooltipText={<InnTooltipText />}
          testId='personalDocuments.inn'
          hasTooltip
        />
      </TwoColumnContainer>
    </>
  );
});
